import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, DeleteOutline, EditOutlined } from "@mui/icons-material";

import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import SearchInput from "../../components/SearchInput";

export const CAPABILITY_TYPES = [
  { label: "HTTP Call",    value: "http_call",    description: "Make HTTP API calls" },
  { label: "RAG API Tool", value: "rag_api_tool", description: "RAG capability with knowledge base filtering" },
  { label: "MCP Client",  value: "mcp_client",   description: "Connect to MCP servers" },
  { label: "LLM Tool",    value: "llm_tool",     description: "Direct LLM interaction capability" },
];

export const CAPABILITY_STATUSES = ["draft", "active", "inactive", "archived"];

const CAPABILITY_TYPE_COLORS = {
  http_call:    { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
  rag_api_tool: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
  mcp_client:   { bg: "#f5f3ff", border: "#ddd6fe", text: "#6d28d9" },
  llm_tool:     { bg: "#fff7ed", border: "#fed7aa", text: "#c2410c" },
};

const COL_HEADER_SX = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: SLATE[500],
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

function TypeBadge({ type }) {
  const colors = CAPABILITY_TYPE_COLORS[type] || { bg: SLATE[100], border: SLATE[300], text: SLATE[600] };
  const label = CAPABILITY_TYPES.find((t) => t.value === type)?.label || type;
  return (
    <Box sx={{ px: "6px", py: "2px", bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: "4px", display: "inline-flex" }}>
      <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: colors.text, letterSpacing: "0.04em" }}>
        {label}
      </Typography>
    </Box>
  );
}

function StatusBadge({ status }) {
  const isActive = status === "active";
  return (
    <Box sx={{ px: "6px", py: "2px", bgcolor: isActive ? "#f0fdf4" : SLATE[100], border: `1px solid ${isActive ? "#bbf7d0" : SLATE[300]}`, borderRadius: "4px", display: "inline-flex" }}>
      <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: isActive ? "#15803d" : SLATE[500], letterSpacing: "0.04em", textTransform: "capitalize" }}>
        {status}
      </Typography>
    </Box>
  );
}

export default function CapabilitiesStore() {
  const navigate = useNavigate();
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ capability_type: "", status: "", search: "" });
  const [confirmCapability, setConfirmCapability] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPageMeta({
      description: "Manage reusable capability templates that can be attached to agents.",
      actions: (
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
          onClick={() => navigate("/admin/capabilities/new")}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
        >
          Create Capability
        </Button>
      ),
    });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCapabilities();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCapabilities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.capability_type) params.append("capability_type", filters.capability_type);
      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);
      const url = `/v2/capabilities${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await makeRequest(url, { method: "GET", authTokens: tokens });
      setCapabilities(response.capabilities || []);
    } catch {
      // error already toasted by useApiCall
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmCapability) return;
    setDeleting(true);
    try {
      await makeRequest(`/v2/capabilities/${confirmCapability.id}`, { method: "DELETE", authTokens: tokens });
      setCapabilities((prev) => prev.filter((c) => c.id !== confirmCapability.id));
      setConfirmCapability(null);
    } catch {
      // error already toasted by useApiCall
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
        <SearchInput
          value={filters.search}
          onChange={(val) => setFilters((p) => ({ ...p, search: val }))}
          placeholder="Search capabilities..."
        />
        <TextField
          select
          size="small"
          value={filters.capability_type}
          onChange={(e) => setFilters((p) => ({ ...p, capability_type: e.target.value }))}
          sx={{ ...INPUT_SX, minWidth: 160 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All Types</MenuItem>
          {CAPABILITY_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={filters.status}
          onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
          sx={{ ...INPUT_SX, minWidth: 140 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {CAPABILITY_STATUSES.map((s) => (
            <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading capabilities..." />
      ) : (
        <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", overflow: "hidden" }}>
          {/* Column headers */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "200px 120px 1fr 90px 72px",
              px: 3,
              py: 1.5,
              borderBottom: `1px solid ${SLATE[200]}`,
              bgcolor: SLATE[50],
            }}
          >
            <Typography sx={COL_HEADER_SX}>Name</Typography>
            <Typography sx={COL_HEADER_SX}>Type</Typography>
            <Typography sx={COL_HEADER_SX}>Description</Typography>
            <Typography sx={COL_HEADER_SX}>Status</Typography>
            <Typography sx={{ ...COL_HEADER_SX, textAlign: "right" }}>Actions</Typography>
          </Box>

          {capabilities.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10, width: "100%" }}>
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>⚙️</Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
                No capabilities found
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[500], mb: 2.5 }}>
                Create your first capability template to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/admin/capabilities/new")}
                sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
              >
                Create Capability
              </Button>
            </Box>
          ) : (
            capabilities.map((capability, idx) => (
              <Box
                key={capability.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "200px 120px 1fr 90px 72px",
                  px: 3,
                  alignItems: "center",
                  minHeight: 60,
                  borderBottom: idx < capabilities.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                  "&:hover": { bgcolor: SLATE[50] },
                }}
              >
                {/* Name */}
                <Box sx={{ pr: 2, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: SLATE[900],
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {capability.display_name || capability.name}
                  </Typography>
                  {capability.display_name && (
                    <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {capability.name}
                    </Typography>
                  )}
                </Box>

                {/* Type */}
                <Box>
                  <TypeBadge type={capability.capability_type} />
                </Box>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: SLATE[500],
                    pr: 2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {capability.description || "—"}
                </Typography>

                {/* Status */}
                <Box>
                  <StatusBadge status={capability.status} />
                </Box>

                {/* Actions */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/admin/capabilities/${capability.id}/edit`)}
                    sx={{ color: SLATE[400], "&:hover": { color: INDIGO[600], bgcolor: "transparent" } }}
                  >
                    <EditOutlined sx={{ fontSize: "1rem" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setConfirmCapability(capability)}
                    sx={{ color: SLATE[400], "&:hover": { color: "#ef4444", bgcolor: "transparent" } }}
                  >
                    <DeleteOutline sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(confirmCapability)} onClose={() => !deleting && setConfirmCapability(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
          Delete Capability
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem", color: SLATE[600] }}>
            Are you sure you want to delete <strong>{confirmCapability?.display_name || confirmCapability?.name}</strong>? This will fail if the capability is attached to any agents.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmCapability(null)}
            disabled={deleting}
            variant="outlined"
            size="small"
            sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="contained"
            size="small"
            sx={{ bgcolor: "#ef4444", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#dc2626", boxShadow: "none" } }}
          >
            {deleting ? <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Helpers used by ManageCapability
export const getCapabilityTypes = () => CAPABILITY_TYPES;
export const getCapabilityStatuses = () => CAPABILITY_STATUSES;

export const validateCapabilityConfiguration = (capabilityType, config) => {
  const errors = [];
  switch (capabilityType) {
    case "http_call":
      if (!config.url) errors.push("URL is required for HTTP call capabilities");
      if (!config.method) errors.push("HTTP method is required");
      break;
    case "rag_api_tool":
      if (!config.url) errors.push("URL is required for RAG API capabilities");
      if (!config.method) errors.push("HTTP method is required");
      break;
    case "mcp_client":
      if (!config.mcp_server_url) errors.push("MCP server URL is required");
      if (!config.mcp_tool_name) errors.push("MCP capability name is required");
      break;
    default:
      break;
  }
  return { valid: errors.length === 0, errors };
};
